<template>
  <div class="min-h-full px-3 sm:px-4 py-6">
    <div v-if="isLoading" class="w-full max-w-7xl mx-auto">
      <div class="animate-pulse space-y-8">
        <div class="h-12 w-2/3 rounded-2xl bg-slate-200/80 dark:bg-slate-800/60"></div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div v-for="i in 4" :key="i" class="h-40 rounded-3xl bg-slate-200/80 dark:bg-slate-800/60"></div>
        </div>
        <div class="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div class="lg:col-span-3 h-96 rounded-3xl bg-slate-200/80 dark:bg-slate-800/60"></div>
          <div class="lg:col-span-2 h-96 rounded-3xl bg-slate-200/80 dark:bg-slate-800/60"></div>
        </div>
      </div>
    </div>

    <div v-else class="w-full max-w-7xl mx-auto">
      <header class="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-5">
        <div class="space-y-1">
          <h1 class="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
            Olá, <span class="bg-clip-text text-transparent bg-gradient-to-r from-[#7B2CBF] to-[#A367E1]">{{ nomeUsuario
              || 'Usuário' }}</span>
          </h1>
          <p class="text-sm text-slate-500 dark:text-slate-400">
            Uma visão clara e elegante da sua saúde financeira.
          </p>
        </div>
        <div class="flex flex-wrap items-center gap-3">
          <div
            class="inline-flex rounded-2xl ring-1 ring-black/5 dark:ring-white/10 bg-white/70 dark:bg-[#240046]/60 backdrop-blur p-1">
            <button v-for="intervalo in ['3m', '6m', '12m']" :key="intervalo" @click="selectedInterval = intervalo"
              class="px-4 py-1.5 rounded-xl text-[13px] font-semibold transition-all" :class="selectedInterval === intervalo
                ? 'bg-gradient-to-r from-[#7B2CBF] to-[#5A189A] text-white shadow-lg shadow-[#5A189A]/20'
                : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/40'">
              {{ intervalo.replace('m', 'M') }}
            </button>
          </div>
          <div class="flex items-center gap-2">
            <button @click="showScenarioModal = true" class="ui-btn" aria-label="Abrir simulação de cenários">
              <span class="material-symbols-outlined text-lg">query_stats</span>
              <span class="hidden sm:inline text-sm font-semibold">Cenários</span>
            </button>
            <button @click="exportToPDF" class="ui-btn" aria-label="Exportar para PDF">
              <span class="material-symbols-outlined text-lg">picture_as_pdf</span>
              <span class="hidden sm:inline text-sm font-semibold">PDF</span>
            </button>
          </div>
        </div>
      </header>

      <section class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div class="tile">
          <div class="flex items-start justify-between">
            <h3 class="tile-label">Saúde Financeira</h3>
            <div class="h-10 w-10 grid place-items-center rounded-xl ring-1 ring-black/5 dark:ring-white/10"
              :style="{ backgroundColor: `${saudeFinanceira.color}15` }">
              <span class="material-symbols-outlined text-xl" :style="{ color: saudeFinanceira.color }">shield</span>
            </div>
          </div>
          <div class="mt-2 flex items-baseline gap-2">
            <span class="text-4xl font-bold text-slate-900 dark:text-slate-100">{{ saudeFinanceira.score.toFixed(0)
              }}</span>
            <span class="text-lg font-medium text-slate-400">/ 100</span>
          </div>
          <p class="mt-1 text-sm font-semibold" :style="{ color: saudeFinanceira.color }">{{ saudeFinanceira.title }}
          </p>
        </div>

        <div class="tile">
          <div class="flex items-start justify-between">
            <h3 class="tile-label">Patrimônio Líquido</h3>
            <div
              class="h-10 w-10 grid place-items-center rounded-xl bg-sky-500/10 ring-1 ring-black/5 dark:ring-white/10">
              <span class="material-symbols-outlined text-xl text-sky-500 dark:text-sky-400">account_balance</span>
            </div>
          </div>
          <div class="mt-2 text-4xl font-bold text-slate-900 dark:text-slate-100">{{ formatCurrency(patrimonioLiquido)
            }}</div>
          <p class="mt-1 text-sm text-slate-500">Ativos − Dívidas</p>
        </div>

        <div class="tile">
          <div class="flex items-start justify-between">
            <h3 class="tile-label">Taxa de Poupança</h3>
            <div
              class="h-10 w-10 grid place-items-center rounded-xl bg-emerald-500/10 ring-1 ring-black/5 dark:ring-white/10">
              <span class="material-symbols-outlined text-xl text-emerald-500 dark:text-emerald-400">savings</span>
            </div>
          </div>
          <div class="mt-2 flex items-baseline gap-1">
            <span class="text-4xl font-bold text-slate-900 dark:text-slate-100">{{ taxaDePoupanca.toFixed(1) }}</span>
            <span class="text-2xl text-slate-500">%</span>
          </div>
          <p class="mt-1 text-sm text-slate-500">Parcela da renda poupada</p>
        </div>

        <div class="tile min-h-[172px]">
          <h3 class="tile-label mb-2">Quitação Mensal</h3>
          <div v-if="diaDeQuitacaoInfo.status === 'concluido'" tabindex="0" role="button" @click="celebrateQuitaçao"
            @keyup.enter="celebrateQuitaçao"
            class="group grid place-items-center text-center rounded-2xl p-3 ring-1 ring-emerald-500/20 bg-emerald-500/10 hover:ring-emerald-500/40 transition">
            <p class="text-5xl font-bold text-emerald-600 dark:text-emerald-400">{{ diaDeQuitacaoInfo.dia }}</p>
            <p class="text-sm font-semibold text-emerald-700 dark:text-emerald-300">{{ diaDeQuitacaoInfo.mes }}</p>
            <p
              class="text-xs text-emerald-700/80 dark:text-emerald-300/90 mt-1 group-hover:scale-105 transition-transform">
              {{ diaDeQuitacaoInfo.mensagem }} 🎉</p>
          </div>
          <div v-else
            class="grid place-items-center text-center rounded-2xl p-3 ring-1 ring-black/5 dark:ring-white/10 bg-slate-100/70 dark:bg-slate-800/40">
            <span class="material-symbols-outlined text-4xl text-emerald-500">check_circle</span>
            <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">Nenhuma despesa pendente.</p>
          </div>
        </div>
      </section>

      <section class="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-8">
        <div class="lg:col-span-3 panel">
          <h3 class="panel-title mb-4">Receitas vs. Despesas ({{ selectedInterval.replace('m', 'M') }})</h3>
          <div class="h-96">
            <Bar v-if="hasMonthlyData" :data="comparativoMensalData" :options="barChartOptions" />
            <div v-else class="empty-state">
              <span class="material-symbols-outlined text-5xl">monitoring</span>
              <span class="mt-1 text-sm">Dados insuficientes para o gráfico.</span>
            </div>
          </div>
        </div>

        <div class="lg:col-span-2 panel">
          <h3 class="panel-title mb-4">Top 5 Despesas do Mês</h3>
          <div class="h-96">
            <apexchart v-if="hasTopCategoriasData" type="donut" :options="apexDonutOptions"
              :series="apexTopCategoriasSeries" height="100%" />
            <div v-else class="empty-state">
              <span class="material-symbols-outlined text-5xl">pie_chart</span>
              <span class="mt-1 text-sm">Sem despesas para exibir.</span>
            </div>
          </div>
        </div>
      </section>

      <section class="panel">
        <h2 class="panel-title mb-4">Suas Metas de Economia</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <div v-for="meta in displayedMetas" :key="meta.id" class="goal group"
            :class="{ 'goal-done': isMetaCompleted(meta) }">
            <div class="flex items-start justify-between">
              <div>
                <p class="text-base font-semibold text-slate-900 dark:text-slate-100">{{ meta.title }}</p>
                <p
                  class="text-sm font-medium text-[#5A189A] dark:text-[#D9BFFB] group-[.goal-done]:text-emerald-600 dark:group-[.goal-done]:text-emerald-400">
                  Meta: {{ formatCurrency(meta.targetAmount) }}
                </p>
              </div>
              <span class="material-symbols-outlined p-2 rounded-xl text-lg ring-1 ring-black/5 dark:ring-white/10"
                :class="isMetaCompleted(meta)
                  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-300'
                  : 'bg-amber-500/10 text-amber-600 dark:text-amber-300'">
                {{ isMetaCompleted(meta) ? 'workspace_premium' : meta.icon }}
              </span>
            </div>
            <div class="mt-3 text-2xl font-bold text-slate-800 dark:text-slate-100">
              {{ formatCurrency(meta.currentAmount) }}
            </div>
            <div class="mt-3 w-full h-2.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
              <div class="h-full rounded-full transition-[width] duration-500"
                :class="isMetaCompleted(meta) ? 'bg-emerald-500' : 'bg-gradient-to-r from-[#7B2CBF] to-[#5A189A]'"
                :style="{ width: `${getProgress(meta)}%` }" />
            </div>
            <p class="mt-1.5 text-right text-xs"
              :class="isMetaCompleted(meta) ? 'font-bold text-emerald-600 dark:text-emerald-300' : 'text-slate-500 dark:text-slate-400'">
              {{ isMetaCompleted(meta) ? 'Meta concluída!' : `${getProgress(meta).toFixed(0)}%` }}
            </p>
          </div>

          <div v-if="!metas.length"
            class="lg:col-span-3 empty-state rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-700 p-8">
            <span class="material-symbols-outlined text-4xl mb-2">add_task</span>
            <p class="text-sm">Você ainda não cadastrou nenhuma meta.</p>
          </div>
        </div>
      </section>
    </div>

    <Teleport to="body">
      <transition name="modal-fade">
        <div v-if="showScenarioModal" @click.self="showScenarioModal = false"
          class="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
          <div
            class="w-full max-w-lg rounded-3xl bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl shadow-2xl ring-1 ring-black/10 dark:ring-white/10 p-6 sm:p-8">
            <h2 class="text-xl sm:text-2xl font-semibold tracking-[-0.01em] text-slate-900 dark:text-slate-100">Cenários
              “E se...”</h2>
            <p class="text-[13px] sm:text-sm text-slate-500 dark:text-slate-400 mt-1 mb-5">Ajuste valores e veja o
              impacto projetado.</p>
            <div class="space-y-4 mb-6">
              <div>
                <label for="scenarioReceita" class="text-sm font-medium text-slate-700 dark:text-slate-300">Acréscimo de
                  Receita Mensal</label>
                <input id="scenarioReceita" type="number" v-model.number="scenarioReceita"
                  class="mt-1 w-full rounded-xl px-3.5 py-2.5 bg-slate-100/80 dark:bg-slate-800/70 ring-1 ring-transparent focus:ring-2 focus:ring-[#5A189A] outline-none transition"
                  placeholder="Ex: 500">
              </div>
              <div>
                <label for="scenarioGasto" class="text-sm font-medium text-slate-700 dark:text-slate-300">Corte de Gasto
                  Mensal</label>
                <input id="scenarioGasto" type="number" v-model.number="scenarioGasto"
                  class="mt-1 w-full rounded-xl px-3.5 py-2.5 bg-slate-100/80 dark:bg-slate-800/70 ring-1 ring-transparent focus:ring-2 focus:ring-[#5A189A] outline-none transition"
                  placeholder="Ex: 300">
              </div>
            </div>
            <div class="rounded-2xl p-4 ring-1 ring-black/5 dark:ring-white/10 bg-slate-100/70 dark:bg-slate-800/50">
              <h3 class="text-base font-semibold text-slate-800 dark:text-slate-200 mb-3">Impacto Projetado</h3>
              <div class="grid grid-cols-2 gap-4 text-center">
                <div>
                  <p class="text-xs text-slate-500 dark:text-slate-400">Novo Score</p>
                  <p class="text-2xl font-bold" :style="{ color: saudeFinanceiraScenario.color }">{{
                    saudeFinanceiraScenario.score.toFixed(0) }}</p>
                </div>
                <div>
                  <p class="text-xs text-slate-500 dark:text-slate-400">Nova Taxa de Poupança</p>
                  <p class="text-2xl font-bold text-emerald-500">{{ taxaDePoupancaScenario.toFixed(1) }}%</p>
                </div>
              </div>
            </div>
            <button @click="showScenarioModal = false"
              class="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#5A189A] hover:bg-[#3C096C] text-white font-semibold py-3 transition">
              Fechar
            </button>
          </div>
        </div>
      </transition>
    </Teleport>

    <transition name="toast-fade">
      <div v-if="notification.show"
        class="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 max-w-sm w-auto px-4 py-3 rounded-2xl shadow-2xl text-white flex items-center gap-3"
        :class="notification.class">
        <span class="material-symbols-outlined">{{ notification.icon }}</span>
        <p class="text-sm font-semibold">{{ notification.message }}</p>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, defineAsyncComponent } from 'vue'
import { useStorage } from '@vueuse/core'
import axios from 'axios'
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import AuthService from '@/service/authService'

const Bar = defineAsyncComponent(() => import('vue-chartjs').then(m => m.Bar))
const Apexchart = defineAsyncComponent(() => import('vue3-apexcharts'))
ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

const nomeUsuario = ref('')
const isLoading = ref(true)
const notification = ref({ show: false, message: '', class: '', icon: '' })
let notificationTimeout = null

const patrimonioLiquido = ref(0)
const receitaMensal = ref(0)
const gastoMensal = ref(0)
const metas = ref([])
const comparativoMensal = ref({ labels: [], receitas: [], despesas: [] })
const topCategorias = ref([])
const selectedInterval = useStorage('dashboard-interval', '6m')
const showScenarioModal = ref(false)
const scenarioReceita = ref(0)
const scenarioGasto = ref(0)

const diaDeQuitacaoInfo = ref({ status: 'pendente', dia: null, mes: null, mensagem: '', deveCelebrar: false })

const showNotification = (message, type = 'success', duration = 4000) => {
  const types = {
    success: { class: 'bg-emerald-600', icon: 'task_alt' },
    info: { class: 'bg-sky-600', icon: 'celebration' },
    error: { class: 'bg-rose-600', icon: 'error' },
    warning: { class: 'bg-amber-500', icon: 'warning' },
  }
  if (notificationTimeout) clearTimeout(notificationTimeout)
  notification.value = { show: true, message, ...types[type] }
  notificationTimeout = setTimeout(() => { notification.value.show = false }, duration)
}

const triggerConfetti = async () => {
  try {
    const confettiModule = await import('canvas-confetti')
    const confetti = confettiModule.default || confettiModule
    confetti({ particleCount: 150, spread: 100, origin: { y: 0.6 } })
  } catch (err) {
    console.error('Falha ao carregar confetti:', err)
  }
}

const celebrateQuitaçao = async () => {
  if (diaDeQuitacaoInfo.value.status === 'concluido') triggerConfetti()
}

const formatCurrency = (v) => (typeof v === 'number' ? v : 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const getProgress = (m) => (!m || m.targetAmount <= 0) ? 0 : Math.min((m.currentAmount / m.targetAmount) * 100, 100)

const displayedMetas = computed(() => metas.value.slice(0, 3))
const hasMonthlyData = computed(() => (comparativoMensal.value.receitas?.length || 0) > 0 || (comparativoMensal.value.despesas?.length || 0) > 0)
const hasTopCategoriasData = computed(() => topCategorias.value.length > 0)
const isMetaCompleted = (m) => m && m.currentAmount >= m.targetAmount

const calcularSaudeFinanceira = (r, d) => {
  if (r === 0) {
    const g = d > 0;
    return { score: g ? 10 : 0, title: g ? 'Alerta' : 'Indefinido', color: g ? '#f97316' : '#94a3b8' };
  }
  const ratio = d / r;
  let scoreValue = Math.max(0, Math.min(100, (1.1 - ratio) * 100));
  let title, color;
  if (ratio < 0.7) { title = 'Excelente'; color = '#7B2CBF'; }
  else if (ratio <= 0.9) { title = 'Sob Controle'; color = '#f59e0b'; }
  else { scoreValue = Math.min(scoreValue, 40); title = 'Atenção Necessária'; color = '#ef4444'; }
  return { score: scoreValue, title, color };
}

const saudeFinanceira = computed(() => calcularSaudeFinanceira(receitaMensal.value, gastoMensal.value))

const calcularTaxaDePoupanca = (r, d) => r > 0 ? ((r - d) / r) * 100 : 0
const taxaDePoupanca = computed(() => calcularTaxaDePoupanca(receitaMensal.value, gastoMensal.value))

// CORREÇÃO 3: Propriedades computadas do cenário re-adicionadas
const saudeFinanceiraScenario = computed(() => calcularSaudeFinanceira((receitaMensal.value + (scenarioReceita.value || 0)), (gastoMensal.value - (scenarioGasto.value || 0))))
const taxaDePoupancaScenario = computed(() => calcularTaxaDePoupanca((receitaMensal.value + (scenarioReceita.value || 0)), (gastoMensal.value - (scenarioGasto.value || 0))))

const processQuitacaoData = (data) => {
  if (data?.status === 'concluido' && data.dataQuitacao) {
    const d = new Date(data.dataQuitacao + 'T00:00:00')
    diaDeQuitacaoInfo.value = {
      status: 'concluido',
      dia: d.getDate(),
      mes: d.toLocaleString('pt-BR', { month: 'short' }).replace('.', '').toUpperCase(),
      mensagem: data.mensagem,
      deveCelebrar: !!data.deveCelebrar
    }
  } else {
    diaDeQuitacaoInfo.value = { status: 'pendente', dia: null, mes: null, mensagem: data?.mensagem || 'Existem despesas pendentes.', deveCelebrar: false }
  }
}

const processData = (dto) => {
  nomeUsuario.value = dto.nomeUsuario || 'Usuário'
  patrimonioLiquido.value = dto.patrimonioLiquido || 0
  receitaMensal.value = dto.receitaMensal || 0
  gastoMensal.value = dto.gastoMensal || 0
  metas.value = dto.metas || []
  comparativoMensal.value = dto.comparativoMensal || { labels: [], receitas: [], despesas: [] }
  topCategorias.value = dto.top5Categorias || []
  if (dto.diaDeQuitacao) {
    processQuitacaoData(dto.diaDeQuitacao)
    if (dto.diaDeQuitacao.deveCelebrar) {
      triggerConfetti()
      showNotification('Parabéns! Todas as despesas do mês foram quitadas.', 'info')
    }
  }
}

const fetchData = async () => {
  isLoading.value = true
  const user = AuthService.getCurrentUser()
  if (!user?.token) { isLoading.value = false; return }
  const cfg = { headers: { Authorization: `Bearer ${user.token}` } }
  try {
    const { data } = await axios.get(`https://backend.controlease.com.br/api/dashboard?interval=${selectedInterval.value}`, cfg)
    processData(data)
  } catch (err) {
    showNotification('Não foi possível carregar os dados do dashboard.', 'error')
    console.error('Erro ao buscar dados do dashboard:', err)
  } finally {
    isLoading.value = false
  }
}

watch(selectedInterval, (nv, ov) => { if (nv !== ov) fetchData() })

onMounted(() => {
  if (AuthService.getCurrentUser()?.token) fetchData()
  else isLoading.value = false
})

const exportToPDF = () => {
  const doc = new jsPDF()
  const user = nomeUsuario.value || 'Usuário'
  doc.text(`Dashboard Financeiro - ${user}`, 14, 15)
  doc.text(`Período: Últimos ${selectedInterval.value.replace('m', ' meses')}`, 14, 22)
  const tableData = comparativoMensal.value.labels.map((label, i) => [
    label,
    formatCurrency(comparativoMensal.value.receitas[i]),
    formatCurrency(comparativoMensal.value.despesas[i])
  ])
  doc.autoTable({ startY: 30, head: [['Mês', 'Receitas', 'Despesas']], body: tableData, theme: 'striped' })
  doc.save(`dashboard_${user.toLowerCase()}_${new Date().toISOString().slice(0, 10)}.pdf`)
}

const comparativoMensalData = computed(() => ({
  labels: comparativoMensal.value.labels,
  datasets: [
    { label: 'Receitas', backgroundColor: '#22c55e', data: comparativoMensal.value.receitas, borderRadius: 6 },
    { label: 'Despesas', backgroundColor: '#ef4444', data: comparativoMensal.value.despesas, borderRadius: 6 }
  ]
}))

const barChartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: { beginAtZero: true, grid: { color: 'rgba(148, 163, 184, 0.1)' }, ticks: { color: '#94a3b8' } },
    x: { grid: { display: false }, ticks: { color: '#94a3b8' } }
  },
  plugins: {
    legend: { position: 'top', align: 'end', labels: { color: '#94a3b8', boxWidth: 12, padding: 20 } },
    tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}` } }
  }
}))

const apexTopCategoriasSeries = computed(() => topCategorias.value.map(c => c.valor))
const apexDonutOptions = computed(() => ({
  chart: { type: 'donut' },
  labels: topCategorias.value.map(c => c.categoria),
  colors: ['#7B2CBF', '#60a5fa', '#34d399', '#facc15', '#a78bfa'],
  dataLabels: { enabled: false },
  legend: {
    position: 'bottom',
    horizontalAlign: 'center',
    labels: { colors: '#94a3b8' },
    markers: { width: 10, height: 10 },
    itemMargin: { horizontal: 8, vertical: 5 }
  },
  tooltip: {
    y: { formatter: (v) => formatCurrency(v), title: { formatter: (s) => s } },
    theme: 'dark'
  },
  plotOptions: {
    pie: {
      donut: {
        labels: {
          show: true,
          total: {
            show: true,
            label: 'Total Gasto',
            color: '#94a3b8',
            formatter: (w) => formatCurrency(w.globals.seriesTotals.reduce((a, b) => a + b, 0))
          }
        }
      }
    }
  }
}))
</script>

<style scoped>
.ui-btn {
  @apply inline-flex items-center gap-2 rounded-2xl px-4 py-2.5 bg-white/70 dark:bg-[#240046]/60 text-slate-700 dark:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-[#240046]/90 ring-1 ring-black/5 dark:ring-white/10 transition shadow-sm;
}

.tile {
  @apply rounded-3xl p-5 ring-1 ring-black/5 dark:ring-white/10 bg-white/70 dark:bg-[#240046]/60 backdrop-blur-lg shadow-xl shadow-slate-200/30 dark:shadow-black/20;
}

.tile-label {
  @apply text-sm font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400;
}

.panel {
  @apply rounded-3xl p-6 ring-1 ring-black/5 dark:ring-white/10 bg-white/70 dark:bg-[#240046]/60 backdrop-blur-lg shadow-xl shadow-slate-200/30 dark:shadow-black/20;
}

.panel-title {
  @apply text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100;
}

.goal {
  @apply p-5 rounded-2xl ring-1 ring-black/5 dark:ring-white/10 bg-slate-100/70 dark:bg-[#10002B]/50 transition;
}

.goal-done {
  @apply bg-emerald-500/10 ring-emerald-500/20;
}

.empty-state {
  @apply grid place-items-center h-full text-slate-500 dark:text-slate-400 text-center;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity .25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: all .3s ease-out;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translateY(14px) translateX(-50%);
}
</style>